import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { SectionHeader } from '../components/ui';
import { PROJECT_STATUS } from '../utils/schema';

export default function ProjectsPage() {
  const projects = useGrowthPlannerStore((s) => s.projects);
  const routes = useGrowthPlannerStore((s) => s.routes);
  const addProject = useGrowthPlannerStore((s) => s.addProject);
  const updateProject = useGrowthPlannerStore((s) => s.updateProject);
  const deleteProject = useGrowthPlannerStore((s) => s.deleteProject);
  const linkProjectToRoute = useGrowthPlannerStore((s) => s.linkProjectToRoute);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', techStack: '', demonstratedSkills: '',
    links: '', documentation: '', status: 'planning',
  });

  const handleCreate = () => {
    if (!form.name.trim()) return;
    addProject({
      name: form.name,
      description: form.description,
      techStack: form.techStack.split(',').map((s) => s.trim()).filter(Boolean),
      demonstratedSkills: form.demonstratedSkills.split(',').map((s) => s.trim()).filter(Boolean),
      links: form.links.split('\n').map((s) => s.trim()).filter(Boolean),
      documentation: form.documentation,
      status: form.status,
    });
    setForm({ name: '', description: '', techStack: '', demonstratedSkills: '', links: '', documentation: '', status: 'planning' });
    setDialogOpen(false);
  };

  return (
    <Box>
      <SectionHeader
        title="Proyectos"
        subtitle="Portfolio de proyectos vinculados a tus rutas profesionales"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Nuevo Proyecto
          </Button>
        }
      />

      {projects.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">Documenta proyectos que demuestren tus habilidades.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 2 }}>
            Crear proyecto
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} key={project.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6" fontWeight={600}>{project.name}</Typography>
                      <Chip label={PROJECT_STATUS[project.status]?.label} size="small" color={PROJECT_STATUS[project.status]?.color} sx={{ mt: 0.5 }} />
                    </Box>
                    <IconButton size="small" color="error" onClick={() => deleteProject(project.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    {project.description}
                  </Typography>
                  {project.techStack?.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                      {project.techStack.map((t) => <Chip key={t} label={t} size="small" variant="outlined" />)}
                    </Stack>
                  )}
                  {project.demonstratedSkills?.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                      {project.demonstratedSkills.map((s) => <Chip key={s} label={s} size="small" color="primary" variant="outlined" />)}
                    </Stack>
                  )}
                  {project.links?.length > 0 && (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      {project.links.map((link, i) => (
                        <Typography key={i} variant="caption" component="a" href={link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 14 }} /> {link}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  <TextField
                    select size="small" label="Estado" value={project.status}
                    onChange={(e) => updateProject(project.id, { status: e.target.value })}
                    sx={{ minWidth: 150, mr: 1 }}
                  >
                    {Object.entries(PROJECT_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                  </TextField>
                  <TextField
                    select size="small" label="Vincular a ruta" value=""
                    onChange={(e) => linkProjectToRoute(project.id, e.target.value)}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="" disabled>Seleccionar ruta</MenuItem>
                    {routes.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </TextField>
                  {project.routeIds?.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                      {project.routeIds.map((rid) => {
                        const r = routes.find((rt) => rt.id === rid);
                        return r ? <Chip key={rid} label={r.name} size="small" /> : null;
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Proyecto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Descripción" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <TextField label="Stack tecnológico (coma)" value={form.techStack} onChange={(e) => setForm({ ...form, techStack: e.target.value })} fullWidth />
            <TextField label="Habilidades demostradas (coma)" value={form.demonstratedSkills} onChange={(e) => setForm({ ...form, demonstratedSkills: e.target.value })} fullWidth />
            <TextField label="Links (uno por línea)" multiline rows={2} value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} fullWidth />
            <TextField label="Documentación" multiline rows={3} value={form.documentation} onChange={(e) => setForm({ ...form, documentation: e.target.value })} fullWidth />
            <TextField select label="Estado" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} fullWidth>
              {Object.entries(PROJECT_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name.trim()}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
